import classNames from 'classnames';
import { observer } from 'mobx-react-lite';
import { FC } from 'react';

import { MemberDevice } from '@gem/call/CallStore/Member/MemberDevice';
import { CallMemberStatus } from '@gem/call/Model/callModel.types';
import { Avatar, AvatarTypes } from '@gem/call/Removable/components/Avatar/Avatar';
import { ActiveSpeakerIcon } from '@gem/call/components/ActiveSpeakerIcon/ActiveSpeakerIcon';
import { IcRaisedHand16Icon } from '@gem/icons';
import { useTranslation } from '@gem/modules/i18n';
import { IconButton, IconViewSizes, IconView } from '@gem/ui-kit';

import { MutedBadge } from '../../MutedBadge/MutedBadge';
import { useMemberCardPresenter } from './MemberCard.presenter';
import styles from './MemberCard.styles.m.css';

const i18nState = {
  [CallMemberStatus.NotOnCall]: 'calls_not_on_call',
  [CallMemberStatus.OnCall]: 'calls_on_call',
  [CallMemberStatus.Declined]: 'calls_declined',
  [CallMemberStatus.Left]: 'calls_left',
};

const stateUserColorMap = {
  [CallMemberStatus.NotOnCall]: 'notOnCall',
  [CallMemberStatus.OnCall]: 'onCall',
  [CallMemberStatus.Declined]: 'declined',
  [CallMemberStatus.Left]: 'left',
};

interface IMemberCardProps {
  memberDevice: MemberDevice;
}

export const MemberCard: FC<IMemberCardProps> = observer(({ memberDevice }) => {
  const { t } = useTranslation();
  const presenter = useMemberCardPresenter(memberDevice);

  return (
    <div
      id={memberDevice.deviceId ? String(memberDevice.deviceId) : memberDevice.memberId}
      className={styles.memberCardContainer}
    >
      <div className={styles.avatarContainer}>
        <Avatar
          type={AvatarTypes.Round_10}
          title={memberDevice.member.displayName}
          url={memberDevice.member.avatarUrl}
          backgroundColorIndex={memberDevice.member.avatarColorIndex}
        />
        {memberDevice.isAudioMuted && <MutedBadge className={styles.mutedBadge} />}
      </div>
      <div className={styles.content}>
        <div className={styles.title}>
          <span className={styles.titleName}>{memberDevice.member.displayName}</span>&nbsp;
          {memberDevice.member.isInitiator && `(${t('call_initiator')})`}
          {presenter.isShowSpeakerRole &&
            !memberDevice.member.isInitiator &&
            memberDevice.member.isAdmin &&
            `(${t('webinar_speaker')})`}
        </div>
        <div className={classNames(styles.subtitle, styles[stateUserColorMap[memberDevice.state]])}>
          {t(i18nState[memberDevice.state])}
          {memberDevice.isActiveSpeaker && <ActiveSpeakerIcon />}
        </div>
      </div>
      <div className={styles.actions}>
        {memberDevice.isHandRaised && (
          <div
            className={classNames(styles.handRaisedContainer, {
              [styles.handRaisedContainerWithNumber]: !!memberDevice.opponent?.handRaisedPosition,
            })}
          >
            <IconView
              Icon={IcRaisedHand16Icon}
              size={IconViewSizes.Small}
              className={classNames(styles.handRaised, {
                [styles.handRaisedWithNumber]: !!memberDevice.opponent?.handRaisedPosition,
              })}
            />
            {!!memberDevice.opponent?.handRaisedPosition && (
              <span className={classNames(styles.handRaisedNumber)}>
                {memberDevice.opponent.handRaisedPosition}
              </span>
            )}
          </div>
        )}
        {presenter.actions.map((action) => (
          <IconButton
            key={action.key}
            Icon={action.icon}
            onClick={action.onClick}
            className={styles.action}
            size={IconViewSizes.Big}
            iconSize={IconViewSizes.Medium}
            theme="11"
          />
        ))}
      </div>
    </div>
  );
});
